/**
 * What this machine is: enrolled or not, connected or not, and what it owes.
 *
 * The token lives in the OS keychain through the shell when there is one, and
 * in `localStorage` when there is not — a browser till is a real deployment and
 * a developer with no Rust toolchain is a more common one. Which of the two
 * answered is on the settings screen, because a token in `localStorage` is a
 * different security posture and somebody should be able to see that it is.
 */
import { create } from 'zustand'
import type { BranchConfig } from '@mgc/protocol'
import { outstanding } from '@/outbox/outbox'
import type { OutboxEntry } from '@/outbox/outbox'
import { inShell, secretGet, secretRemove, secretSet } from '@/shell/native'

const KEY = 'mgc.device-token'

/**
 * The keychain entry, named for the origin that enrolled it.
 *
 * `localStorage`, IndexedDB and the worker's caches are all keyed to the origin
 * by the browser; the OS keychain is not — it is one entry per machine under
 * the shell's `identifier`, whichever deployment the window loads. So a till
 * that once ran a development or staging build carried that deployment's token
 * into production, the server refused it as unenrolled, and the page — reading
 * the refusal as a dead uplink — said «no internet and no saved menu» on a
 * machine that had both. Measured 2026-09-29 on the first production Mac build.
 *
 * The host with its port folded in, because `secrets.rs` allows only
 * `[A-Za-z0-9._-]` and `localhost:4010` is a different deployment from
 * `localhost:4011`.
 */
const keychainKey = (): string => `${KEY}.${location.host.replace(/[^A-Za-z0-9.-]/g, '-')}`

export type TokenSource = 'keychain' | 'local-storage' | 'none'

export async function readToken(): Promise<{ token: string | null; source: TokenSource }> {
  // `pnpm dev` should sell a coffee without an enrolment step. The seed script
  // prints this token; it is a development-only door and `import.meta.env.DEV`
  // is false in every build that reaches a counter.
  if (import.meta.env.DEV) {
    const seeded = (import.meta.env['VITE_DEV_TOKEN'] as string | undefined) ?? 'dev-kiosk-token'
    if (seeded.length > 0) return { token: seeded, source: 'local-storage' }
  }

  if (inShell()) {
    try {
      const token = await secretGet(keychainKey())
      if (token !== null) return { token, source: 'keychain' }
    } catch {
      // The ACL refused, or there is no keychain. Fall through rather than
      // refusing to open: an unenrolled kiosk shows a pairing screen, and a
      // kiosk that cannot read its own token shows the same one.
    }
  }
  try {
    const token = localStorage.getItem(KEY)
    return token === null ? { token: null, source: 'none' } : { token, source: 'local-storage' }
  } catch {
    return { token: null, source: 'none' }
  }
}

export async function writeToken(token: string): Promise<TokenSource> {
  if (inShell()) {
    try {
      await secretSet(keychainKey(), token)
      return 'keychain'
    } catch {
      // fall through
    }
  }
  try {
    localStorage.setItem(KEY, token)
    return 'local-storage'
  } catch {
    return 'none'
  }
}

export async function clearToken(): Promise<void> {
  if (inShell()) await secretRemove(keychainKey()).catch(() => {})
  try {
    localStorage.removeItem(KEY)
  } catch {
    /* a private window; nothing to remove */
  }
}

export interface SessionState {
  ready: boolean
  token: string | null
  tokenSource: TokenSource
  branch: BranchConfig | null
  deviceName: string | null
  socketConnected: boolean
  /** Unacknowledged payments. The number nobody wants to be non-zero. */
  outbox: readonly OutboxEntry[]
  /** Set while a staff PIN has authorised an action. Never a session. */
  staffId: string | null
  staffName: string | null
}

export interface SessionActions {
  setReady: (ready: boolean) => void
  setToken: (token: string | null, source: TokenSource) => void
  setBranch: (branch: BranchConfig | null) => void
  setDeviceName: (name: string | null) => void
  setSocketConnected: (connected: boolean) => void
  setOutbox: (entries: readonly OutboxEntry[]) => void
  /**
   * Re-read the outbox from IndexedDB.
   *
   * The store is a mirror of a durable thing, so anything that writes to that
   * thing has to say so. Resolving a stuck claim is the first such writer that
   * is not the payment path itself.
   */
  refreshOutbox: () => Promise<void>
  authorise: (staffId: string, name: string) => void
  deauthorise: () => void
}

export const useSession = create<SessionState & SessionActions>()((set) => ({
  ready: false,
  token: null,
  tokenSource: 'none',
  branch: null,
  deviceName: null,
  socketConnected: false,
  outbox: [],
  staffId: null,
  staffName: null,

  setReady: (ready) => set({ ready }),
  setToken: (token, tokenSource) => set({ token, tokenSource }),
  setBranch: (branch) => set({ branch }),
  setDeviceName: (deviceName) => set({ deviceName }),
  setSocketConnected: (socketConnected) => set({ socketConnected }),
  setOutbox: (outbox) => set({ outbox }),
  refreshOutbox: async () => set({ outbox: await outstanding() }),
  authorise: (staffId, staffName) => set({ staffId, staffName }),
  deauthorise: () => set({ staffId: null, staffName: null })
}))
